import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AccountBalanceWallet, Paid, People, Refresh, Security, Timeline, Visibility } from "@mui/icons-material";
import { getDashboardSummary, fetchIncidents, detectIncidents } from "../services/owdedApi";
import { dateOnly, money, number, percent } from "./format";
import { EmptyState, MetricCard, Modal, StatusPill } from "./ui";
import { currentMonthRange, rangeForMode } from "./listUtils";

const fallbackSummary = {
  portfolio: { total_clients: 0, new_clients: 0, clients_servis: 0, clients_non_servis: 0 },
  investments: { total_investments: 0, investments_pending: 0, by_currency: {} },
  recoveries: { by_currency: {} },
  finance: { by_currency: {} },
  risk: { late_clients: 0, default_rate: 0, by_currency: {} },
  charts: { collectionsByProduct: [], collectionsByDay: [], investmentsByStatus: [], recoveriesByInvestment: [] },
};

const currencies = ["CDF", "USD"];

function Dashboard() {
  const [summary, setSummary] = useState(fallbackSummary);
  const [incidents, setIncidents] = useState([]);
  const [periodMode, setPeriodMode] = useState("MONTH");
  const [period, setPeriod] = useState(currentMonthRange());
  const [notice, setNotice] = useState("");
  const [incidentDetail, setIncidentDetail] = useState(null);
  const activeRange = rangeForMode(periodMode, period.start_date, period.end_date);
  const { start_date: startDate, end_date: endDate } = activeRange;

  const load = useCallback(async () => {
    try {
      const data = await getDashboardSummary({ start_date: startDate, end_date: endDate });
      setSummary({ ...fallbackSummary, ...data });
      setIncidents(await fetchIncidents({ status: "OPEN" }));
    } catch (err) {
      setNotice("Connexion API indisponible. Les indicateurs restent a zero.");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const runDetection = async () => {
    const result = await detectIncidents();
    setNotice(`${result.inserted || 0} incident(s) detecte(s).`);
    await load();
  };

  const byProduct = summary.charts?.collectionsByProduct || [];
  const byDay = summary.charts?.collectionsByDay || [];
  const byStatus = summary.charts?.investmentsByStatus || [];
  const byInvestment = summary.charts?.recoveriesByInvestment || [];
  const productMax = Math.max(...byProduct.map((item) => Number(item.value || 0)), 1);
  const dayMax = Math.max(...byDay.map((item) => Number(item.value || 0)), 1);
  const statusMax = Math.max(...byStatus.map((item) => Number(item.value || 0)), 1);
  const investmentMax = Math.max(...byInvestment.map((item) => Number(item.value || 0)), 1);
  const investmentsByCurrency = summary.investments?.by_currency || {};
  const recoveriesByCurrency = summary.recoveries?.by_currency || {};
  const financeByCurrency = summary.finance?.by_currency || {};
  const riskByCurrency = summary.risk?.by_currency || {};
  const totalRecoverable = currencies.reduce((sum, currency) => sum + Number(recoveriesByCurrency?.[currency]?.amount_to_recover || 0), 0);
  const totalRecovered = currencies.reduce((sum, currency) => sum + Number(recoveriesByCurrency?.[currency]?.recovered_total || 0), 0);
  const globalRecoveryRate = totalRecoverable > 0 ? Math.min((totalRecovered / totalRecoverable) * 100, 100) : 0;
  const incidentGroups = useMemo(() => {
    const groups = new Map();
    incidents.forEach((incident) => {
      const key = `${incident.client_id || incident.client_name || "client"}-${incident.investment_id || incident.investment_reference || "investment"}`;
      const currency = incident.currency || "CDF";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          client_name: incident.client_name,
          investment_reference: incident.investment_reference,
          currency,
          days_late: 0,
          amountByCurrency: { CDF: 0, USD: 0 },
          incidentTypes: new Set(),
          items: [],
        });
      }
      const group = groups.get(key);
      group.days_late += Number(incident.days_late || 0);
      group.amountByCurrency[currency] = Number(group.amountByCurrency[currency] || 0) + Number(incident.amount || 0);
      group.incidentTypes.add(incident.incident_type || "Incident");
      group.items.push(incident);
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      incident_types: Array.from(group.incidentTypes),
    }));
  }, [incidents]);
  const metricStack = (source, field) => (
    <span className="metric-stack">
      {currencies.map((currency) => <span key={currency}>{money(source?.[currency]?.[field], currency)}</span>)}
    </span>
  );

  return (
    <main className="page-content">
      <div className="page-head">
        <div>
          <h1>Tableau de bord executif</h1>
          <p>Portefeuille, investissements, recouvrements, tresorerie et risque.</p>
        </div>
        <button className="ghost-btn" type="button" onClick={runDetection}>
          <Security /> Detecter incidents
        </button>
      </div>

      <section className="panel form-panel">
        <div className="table-toolbar compact-toolbar">
          <div className="period-controls">
            <select value={periodMode} onChange={(event) => setPeriodMode(event.target.value)}>
              <option value="DAY">Jour</option>
              <option value="WEEK">Semaine</option>
              <option value="MONTH">Mois</option>
              <option value="PERIOD">Periode</option>
            </select>
            {periodMode === "PERIOD" && (
              <>
                <input type="date" value={period.start_date} onChange={(event) => setPeriod((current) => ({ ...current, start_date: event.target.value }))} />
                <input type="date" value={period.end_date} onChange={(event) => setPeriod((current) => ({ ...current, end_date: event.target.value }))} />
              </>
            )}
          </div>
          <button className="primary-btn" type="button" onClick={load}><Refresh /> Actualiser</button>
        </div>
      </section>

      {notice && <p className="notice">{notice}</p>}

      <br />
      <div className="metric-grid">
        <MetricCard label="Clients" value={number(summary.portfolio?.total_clients)} hint={`${number(summary.portfolio?.new_clients)} nouveaux`} icon={<People />} />
        <MetricCard label="Souscrit" value={metricStack(investmentsByCurrency, "amount_subscribed")} hint={`${number(summary.investments?.investments_pending)} en attente`} icon={<Paid />} />
        <MetricCard label="Recouvre periode" value={metricStack(recoveriesByCurrency, "recovered_today")} hint="CDF / USD" icon={<Timeline />} />
        <MetricCard label="Tresorerie" value={metricStack(financeByCurrency, "global_treasury")} hint="Solde par devise" icon={<AccountBalanceWallet />} />
      </div>

      <div className="chart-grid">
        <section className="panel form-panel">
          <h2 className="section-title">Recouvrement</h2>
          <div className="currency-split-grid">
            {currencies.map((currency) => {
              const row = recoveriesByCurrency[currency] || {};
              const width = Math.min(Number(row.recovery_rate || 0), 100);
              return (
                <div className="currency-panel" key={currency}>
                  <h3>{currency}</h3>
                  <div className="finance-strip">
                    <div className="mini-card"><span>A recouvrer</span><strong>{money(row.amount_to_recover, currency)}</strong></div>
                    <div className="mini-card"><span>Deja recouvre</span><strong>{money(row.recovered_total, currency)}</strong></div>
                    <div className="mini-card"><span>Restant</span><strong>{money(row.remaining_to_recover, currency)}</strong></div>
                  </div>
                  <div className="progress-track"><span className="progress-value" style={{ width: `${width}%` }} /></div>
                  <small>{percent(row.recovery_rate)} recouvre</small>
                </div>
              );
            })}
          </div>
        </section> 

        <section className="panel form-panel">
          <h2 className="section-title">Risque</h2>
          <div className="finance-strip">
            <div className="mini-card">
              <span>Clients en retard</span>
              <strong>{number(summary.risk?.late_clients)}</strong>
            </div>
            <div className="mini-card">
              <span>Montant en retard</span>
              <strong>{currencies.map((currency) => <span key={currency} className="amount-line">{money(riskByCurrency?.[currency]?.late_amount, currency)}</span>)}</strong>
            </div>
            <div className="mini-card">
              <span>Taux defaut</span>
              <strong>{percent(summary.risk?.default_rate)}</strong>
              <small>{number(summary.risk?.late_investments)} dossier(s) / {number(summary.risk?.active_investments)} actif(s)</small>
            </div>
          </div>
          <div className="risk-chart-grid">
            <div className="risk-mini-chart">
              <h3>Investissements par statut</h3>
              {byStatus.length ? (
                <div className="bar-chart compact">
                  {byStatus.map((item) => (
                    <div className="bar-row" key={`${item.label}-${item.currency}`}>
                      <span>{item.label} {item.currency}</span>
                      <span className="bar-track"><span className="bar-fill" style={{ width: `${(Number(item.value || 0) / statusMax) * 100}%` }} /></span>
                      <strong>{money(item.value, item.currency)}</strong>
                    </div>
                  ))}
                </div>
              ) : <EmptyState label="Aucun investissement sur la periode." />}
            </div>
            <div className="risk-mini-chart">
              <h3>Recouvrements par investissement</h3>
              {byInvestment.length ? (
                <div className="bar-chart compact">
                  {byInvestment.map((item) => (
                    <div className="bar-row" key={`${item.label}-${item.currency}`}>
                      <span>{item.label}</span>
                      <span className="bar-track"><span className="bar-fill" style={{ width: `${(Number(item.value || 0) / investmentMax) * 100}%` }} /></span>
                      <strong>{money(item.value, item.currency)}</strong>
                    </div>
                  ))}
                </div>
              ) : <EmptyState label="Aucun recouvrement sur la periode." />}
            </div>
          </div>
        </section>
      </div>

      <br />

      <section className="panel dashboard-visual-band">
        <div className="ellipse-stat">
          <span className="ellipse-ring" style={{ "--value": `${globalRecoveryRate}%` }}>
            <strong>{percent(globalRecoveryRate)}</strong>
          </span>
          <div>
            <h2>Progression du recouvrement</h2>
            <p>Vue consolidee par devise, sans conversion entre CDF et USD.</p>
          </div>
        </div>
        <div className="histogram-card">
          <div>
            <h2>Histogramme des encaissements</h2>
            <p>Evolution sur la periode selectionnee</p>
          </div>
          <div className="histogram-bars">
            {(byDay.length ? byDay.slice(-10) : Array.from({ length: 10 }, (_, index) => ({ label: index, value: 0 }))).map((item, index) => (
              <span key={`${item.label}-${item.currency || index}`} style={{ height: `${Math.max((Number(item.value || 0) / dayMax) * 100, 8)}%` }} />
            ))}
          </div>
        </div>
      </section>

      <br />

      <div className="chart-grid">
        <section className="panel chart-panel">
          <h2 className="section-title">Cotisations par produit</h2>
          {byProduct.length ? (
            <div className="bar-chart">
              {byProduct.map((item) => (
                <div className="bar-row" key={`${item.label}-${item.currency}`}>
                  <span>{item.label}</span>
                  <span className="bar-track"><span className="bar-fill" style={{ width: `${(Number(item.value || 0) / productMax) * 100}%` }} /></span>
                  <strong>{money(item.value, item.currency)}</strong>
                </div>
              ))}
            </div>
          ) : <EmptyState label="Aucune collecte par produit." />}
        </section>

        <section className="panel chart-panel">
          <h2 className="section-title">Evolution 14 jours</h2>
          {byDay.length ? (
            <div className="bar-chart">
              {byDay.map((item) => (
                <div className="bar-row" key={`${item.label}-${item.currency}`}>
                  <span>{dateOnly(item.label)} {item.currency}</span>
                  <span className="bar-track"><span className="bar-fill" style={{ width: `${(Number(item.value || 0) / dayMax) * 100}%` }} /></span>
                  <strong>{money(item.value, item.currency)}</strong>
                </div>
              ))}
            </div>
          ) : <EmptyState label="Aucune collecte recente." />}
        </section>
      </div>

      <br />

      <section className="panel data-panel">
        <div className="table-toolbar">
          <h2 className="section-title">Incidents ouverts</h2>
          <span>{incidentGroups.length} dossier(s) groupe(s) / {incidents.length} alerte(s)</span>
        </div>
        {incidentGroups.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Investissement</th>
                <th>Incidents</th>
                <th>Retard cumule</th>
                <th>Montant cumule</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incidentGroups.slice(0, 8).map((group) => (
                <tr key={group.key}>
                  <td>{group.client_name}</td>
                  <td>{group.investment_reference}</td>
                  <td>{group.incident_types.join(", ")}<br /><small>{group.items.length} ligne(s)</small></td>
                  <td>{number(group.days_late)} jour(s)</td>
                  <td>
                    {currencies.map((currency) => (
                      <span key={currency} className="amount-line">{money(group.amountByCurrency[currency], currency)}</span>
                    ))}
                  </td>
                  <td><StatusPill value="OPEN" /></td>
                  <td><button className="ghost-btn" type="button" onClick={() => setIncidentDetail(group)}><Visibility /> Voir</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState label="Aucun incident ouvert." />
        )}
      </section>

      {incidentDetail && (
        <Modal
          title={`Incidents - ${incidentDetail.investment_reference}`}
          onClose={() => setIncidentDetail(null)}
        >
          <div className="incident-group-summary">
            <div><span>Client</span><strong>{incidentDetail.client_name}</strong></div>
            <div><span>Retard cumule</span><strong>{number(incidentDetail.days_late)} jour(s)</strong></div>
            <div>
              <span>Montant cumule</span>
              <strong>{currencies.map((currency) => <span key={currency} className="amount-line">{money(incidentDetail.amountByCurrency[currency], currency)}</span>)}</strong>
            </div>
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Type</th><th>Retard</th><th>Montant</th><th>Statut</th></tr></thead>
            <tbody>
              {incidentDetail.items.map((incident) => (
                <tr key={incident.id}>
                  <td>{dateOnly(incident.detected_at)}</td>
                  <td>{incident.incident_type}</td>
                  <td>{number(incident.days_late)} jour(s)</td>
                  <td>{money(incident.amount, incident.currency)}</td>
                  <td><StatusPill value={incident.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </main>
  );
}

export default Dashboard;
